import { axiosInstance } from 'boot/axios';
import { Cookies } from 'quasar';
import Notify from 'src/constants/notify';
import { dataModificationRedirect } from 'src/utils/helpers.js';
import i18n from 'src/i18n/index.js';

import Permissions from 'src/models/permissions.js';
import User from 'src/models/user.js';
import AccountDetails from 'src/models/accountDetails.js';
import VerificationReceiver from 'src/models/verificationReceiver.js';
import { parseJwt, convertToUtcDateFromString } from 'src/utils/helpers';

const idinCookieName = 'idin-session-cookie';

function getLoginErrorMessage(errorData) {
  if (errorData && typeof errorData === 'string') {
    if (
      errorData.startsWith('Incorrect username/password combination.') ||
      errorData.startsWith('Account not activated')
    ) {
      return i18n.global.t('login.notification.form-invalid');
    } else if (errorData.startsWith('Account is temporary blocked')) {
      return i18n.global.t('login.notification.blocked');
    }
    return 'login.notification.default';
  }
}
export function login({ commit, getters }, data) {
  const router = this.$router;
  const p = new Promise(function (resolve, reject) {
    return axiosInstance
      .post('/account/login', data.body)
      .then((response) => {
        commit('setHasPhoneNumber', !getters.verificationReceiver.phoneNumberIsUnknown);
        commit('setIsVerificationCodeNeeded', getters.verificationReceiver.isVerificationCodeNeeded);
        commit('setRememberMe', response.rememberMe);
        commit('setFromPage', 'login');

        if (!getters.isVerificationCodeNeeded || getters.hasPhoneNumber) {
          router.push({
            name: 'verify',
          });
        }
        resolve();
      })
      .catch((error) => {
        if (typeof error.response.data === 'string' && error.response.data.startsWith('Account not activated')) {
          router.push({ name: 'register' });
        } else if (typeof error.response.data === 'string') {
          Notify.create(
            {
              type: 'negative',
              message: getLoginErrorMessage(error.response.data),
            },
            error.response,
          );
        }

        reject(error);
      });
  });

  return p;
}

export function verify({ dispatch, getters }, data) {
  let p;

  if (getters.comesFromLoginPage) {
    p = dispatch('verifyLogin', data);
  } else if (getters.comesFromForgotPage) {
    p = dispatch('verifyForgot', data);
  }

  return p;
}

export function resend({ commit }) {
  return axiosInstance.post('Account/ResendVerificationCode').then((response) => {
    commit('setVerificationReceiver', response.data);
  });
}

export function getAccountDetails({ commit }) {
  return axiosInstance.post('Account/GetAccountDetails').then((response) => {
    commit('setAccountDetails', new AccountDetails(response.data));
  });
}

export function resetUsername({ commit }, payload) {
  const utcDate = convertToUtcDateFromString(payload.birthDate, 'yyyy/mm/dd');
  return axiosInstance
    .post('Account/ResetUsername', {
      policyNumber: payload.policyNumber,
      birthDate: utcDate,
    })
    .then(
      () => {
        commit('setFromPage', 'forgot');
        commit('setFlowType', 'username');

        this.$router.push({
          name: 'verify',
        });
      },
      (error) => {
        if (error.data?.startsWith('Account not activated')) this.$router.push({ name: 'register' });
        resetFlowErrorHandler(commit, error);
      },
    );
}

export function resetPassword({ commit }, payload) {
  const utcDate = convertToUtcDateFromString(payload.birthDate, 'yyyy/mm/dd');
  return axiosInstance
    .post('Account/ResetPassword', {
      username: payload.username,
      birthDate: utcDate,
    })
    .then(
      () => {
        commit('setFromPage', 'forgot');
        commit('setFlowType', 'password');

        this.$router.push({
          name: 'verify',
        });
      },
      (error) => {
        if (error.data?.startsWith('Account not activated')) this.$router.push({ name: 'register' });
        resetFlowErrorHandler(commit, error);
      },
    );
}

function resetFlowErrorHandler(commit, error) {
  if (error.data?.startsWith('Account does not have:')) {
    commit('setHasPhoneNumber', !error.data.includes('PhoneNumber'));
    commit('setHasEmailAddress', !error.data.includes('EmailAddress'));
  } else {
    let message = i18n.global.t('forgot.notification.account-not-found');
    if (error.data?.startsWith('Account not found with Username'))
      message = i18n.global.t('forgot.notification.unknown-username');
    if (error.data?.startsWith('Account not activated'))
      message = i18n.global.t('forgot.notification.account-not-activated');
    if (error.data?.startsWith('Could not send verificationcode'))
      message = i18n.global.t('forgot.notification.incorrect-combination');

    Notify.create(
      {
        color: 'negative',
        message: message,
      },
      error,
    );
  }
}

export function getVerification({ commit }, loginToken) {
  const parsedResponse = parseJwt(loginToken);
  const receiver = JSON.parse(parsedResponse.VerificationReceiver);
  commit('setVerificationReceiver', new VerificationReceiver(receiver));
}

export function updatePassword(_, password) {
  const p = new Promise(function (resolve, reject) {
    return axiosInstance
      .post('Account/UpdatePassword', { Password: password })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });

  return p;
}

export function verifyLogin({ commit }, data) {
  const router = this.$router;

  return new Promise(function (resolve, reject) {
    return axiosInstance
      .post('/account/validateVerificationCode', { verificationCode: data })
      .then(() => {
        commit('news/clearNewsTexts', '', { root: true });

        router.push('/');
        resolve();
      })

      .catch((error) => {
        if (typeof error.response.data === 'string') {
          Notify.create(
            {
              type: 'negative',
              message: i18n.global.t('verify.wrong-code'),
            },
            error.response,
          );
        }
        reject(error);
      });
  });
}

export function verifyForgot({ commit, state }, data) {
  return new Promise(function (resolve, reject) {
    return axiosInstance
      .post('/account/validateVerificationCodeResetFlow', {
        verificationCode: data,
        resetType: state.flowType,
      })
      .then((response) => {
        commit('setEmail', response.data);
        resolve();
      })

      .catch((error) => {
        Notify.create(
          {
            type: 'negative',
            message: i18n.global.t('verify.wrong-code'),
          },
          error.response,
        );

        reject(error);
      });
  });
}

export function parseTokenSetUser({ commit }, token) {
  const parsedResponse = parseJwt(token);

  const permissions = new Permissions(parsedResponse['aevitae/permissions']);
  const user = new User(JSON.parse(parsedResponse['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata']));

  commit('user/setPermissions', permissions, { root: true });
  commit('user/setUser', user, { root: true });
}

export function setToken(_, data) {
  axiosInstance.defaults.headers.common.Authorization = 'Bearer ' + data.token;

  const parsedToken = parseJwt(data.token);
  const issuedAtTime = parsedToken.iat;
  const expirationTime = parsedToken.exp;
  const timeDifference = expirationTime - issuedAtTime;

  const localExpirationTime = new Date().getTime() + timeDifference * 1000;

  // Adding the local expiration with the current local time and token expiration time
  // We use this value for calculating if token is expired. We need to check with local time because local time can differ from server time
  Cookies.set('authorization_token_local_expiration_time', localExpirationTime, {
    path: '/',
    expires: timeDifference + 's',
  });

  if (data.rememberMe) {
    Cookies.set('authorization_token', data.token, {
      expires: 365,
      path: '/',
    });
  } else {
    Cookies.set('authorization_token', data.token, {
      path: '/',
      expires: timeDifference + 's',
    });
  }
}

export async function fetchUser({ dispatch }) {
  var token = Cookies.get('authorization_token');

  if (token) {
    dispatch('parseTokenSetUser', token);
    axiosInstance.defaults.headers.common.Authorization = 'Bearer ' + token;
  }
}

export async function clearStorage() {
  sessionStorage.clear();
  localStorage.removeItem('pdfjs.history');
  localStorage.removeItem('pdfjs.preferences');
  caches.keys().then((names) => {
    for (const name of names) {
      caches.delete(name);
    }
  });
}

export function logout({ dispatch, commit }) {
  dispatch('clearStorage');
  commit('setIsLoggingOff', true);
  commit('user/clearUser', null, { root: true });
  Cookies.remove('authorization_token_local_expiration_time', {
    path: '/',
  });

  Cookies.remove('authorization_token', {
    path: '/',
  });

  location.reload();
}

export function getIdinSession(_, payload) {
  const { isRegister, bankIssuer } = payload;

  return axiosInstance
    .post('Idin/create-session', {
      iDINFlow: isRegister ? 0 : 1,
      bankIssuer: bankIssuer,
      clientBaseUrl: window.location.origin,
    })
    .then((x) => {
      Cookies.set(idinCookieName, x.data.clientCode, {
        expires: x.data.sessionLifetime + 's',
        sameSite: 'Strict',
        secure: true,
      });

      return x;
    });
}

export function verifyIdin({ dispatch }, payload) {
  const { Code, State, Error, ErrorDescription, callbackUrl } = payload;
  let redirectionPage = 'home';

  return axiosInstance
    .get('Idin', {
      params: {
        code: Code,
        state: State,
        error: Error ?? '',
        error_description: ErrorDescription ?? '',
        callbackUrl: callbackUrl,
      },
    })
    .catch((error) => {
      dispatch('catchIdinError', {
        error: error.response,
        state: State,
      });
      redirectionPage = error.response.data.startsWith('Account not found for iDinIdentifier')
        ? 'idin-register'
        : 'login';
    })
    .finally(() => {
      const useReplace = true;
      dataModificationRedirect(redirectionPage, this.$router, useReplace);
    });
}

export function IdinAuthenticate({ dispatch }, payload) {
  const { sessionId } = payload;
  let redirectionPage = 'home';

  let cookieResult = Cookies.get(idinCookieName);
  Cookies.remove(idinCookieName);

  return axiosInstance
    .post('Idin/process-authentication', { sessionId: sessionId, clientCode: cookieResult })
    .catch((error) => {
      dispatch('catchIdinError', {
        error: error.response,
      });
      redirectionPage =
        typeof error.response.data === 'string' &&
        error.response.data.startsWith('Account not found for iDinIdentifier')
          ? 'idin-register'
          : 'login';
    })
    .finally(() => {
      const useReplace = true;
      dataModificationRedirect(redirectionPage, this.$router, useReplace);
    });
}

export function IdinIdentificate({ dispatch }, payload) {
  const { sessionId } = payload;
  let redirectionPage = 'home';

  let cookieResult = Cookies.get(idinCookieName);
  Cookies.remove(idinCookieName);

  return axiosInstance
    .post('Idin/process-identification', { sessionId: sessionId, clientCode: cookieResult })
    .catch((error) => {
      dispatch('catchIdinError', {
        error: error.response,
      });
      redirectionPage = 'idin-register';
    })
    .finally(() => {
      const useReplace = true;
      dataModificationRedirect(redirectionPage, this.$router, useReplace);
    });
}

export function IdinLogError(_, payload) {
  const { sessionId } = payload;

  return axiosInstance.post('Idin/process-error', { sessionId: sessionId }).finally(() => {
    const useReplace = true;
    dataModificationRedirect('login', this.$router, useReplace);
  });
}

export function catchIdinError({ commit }, payload) {
  if (payload.error.status === 400) {
    commit('setHasIdinFailed', true);
    const errorType = isolateIdinErrorType(payload.error.data);
    switch (errorType) {
      case 'Expired':
        commit(
          'setIdinFailureMessage',
          i18n.global.t('idin-login.failure-popup.expired', {
            action: i18n.global.t(`idin-login.failure-popup.${payload.state}`),
          }),
        );
        break;
      case 'Cancelled':
        commit(
          'setIdinFailureMessage',
          i18n.global.t('idin-login.failure-popup.cancelled', {
            action: i18n.global.t(`idin-login.failure-popup.${payload.state}`),
          }),
        );
        break;
      case 'RegisterAccountNotFound':
        commit('setIdinFailureMessage', i18n.global.t('idin-login.failure-popup.register-account-not-found'));
        break;
      case 'IdinLoginNotActive':
        commit('setIdinFailureMessage', i18n.global.t('idin-login.failure-popup.should-activate'));
        break;
      case 'ClientCodeEmpty':
        commit('setIdinFailureMessage', i18n.global.t('idin-login.failure-popup.failure'));
        commit('setHasIdinFailureInstructions', true);
        break;
      default:
        commit('setIdinFailureMessage', i18n.global.t('idin-login.failure-popup.failure'));
    }
  } else if (payload.error.status === 422) {
    commit('setHasIdinFailed', true);
    commit('setIdinFailureMessage', i18n.global.t('idin-login.failure-popup.failure'));
    commit('setHasIdinFailureInstructions', true);
  }
}

function isolateIdinErrorType(errorString) {
  if (typeof errorString !== 'string' && errorString?.errors['ClientCode']) return 'ClientCodeEmpty';
  if (errorString === 'Account not found for iDin register') return 'RegisterAccountNotFound';
  if (errorString?.startsWith('Account not found for iDinIdentifier')) return 'IdinLoginNotActive';
  return errorString.includes('usercancel:') ? errorString.split('usercancel:')[1].slice(0, -2) : 'failed';
}

export function updatePhoneNumber({ commit, getters }, payload) {
  return axiosInstance.post('Account/updatePhoneNumber', { phoneNumber: payload }).then(() => {
    commit('setHasPhoneNumber', !getters.verificationReceiver.phoneNumberIsUnknown);

    this.$router.push({
      name: 'verify',
    });
  });
}

export function signTermsOfAgreement() {
  return axiosInstance.post('Account/SignTermsOfAgreement');
}

export function updateAccountDetails({ commit }, payload) {
  return axiosInstance
    .post('account/UpdateAccountDetails', payload)
    .then((response) => {
      if (response.data.usernameExists) {
        commit('setUsernameAlreadyExists', true);
      } else if (!response.data.usernameExists) {
        commit('setUsernameAlreadyExists', false);
        Notify.create({
          progress: true,
          icon: 'done',
          color: 'positive',
          message: i18n.global.t('edit-personal-info.valid'),
        });
        dataModificationRedirect('settings', this.$router);
      }
    })
    .catch((error) => {
      Notify.create(
        {
          color: 'negative',
          message: i18n.global.t('edit-personal-info.error-saving'),
        },
        error.response,
      );
    });
}

export function updateContactDetails(_, payload) {
  return axiosInstance
    .post('account/UpdateContactDetails', payload)
    .then(() => {
      Notify.create({
        progress: true,
        icon: 'done',
        color: 'positive',
        message: i18n.global.t('edit-personal-info.valid'),
      });
      dataModificationRedirect('settings', this.$router);
    })
    .catch((error) => {
      Notify.create(
        {
          color: 'negative',
          message: i18n.global.t('edit-personal-info.error-saving'),
        },
        error.response,
      );
    });
}

export function updateDigitalPopUp(_, payload) {
  return axiosInstance.post('account/UpdateContactDetails', payload);
}

export function setUsernameAlreadyExists({ commit }, payload) {
  commit('setUsernameAlreadyExists', payload);
}

export function setHasIdinFailed({ commit }, payload) {
  commit('setHasIdinFailed', payload);
}

export function clearHasIdinFailed({ commit }) {
  commit('clearHasIdinFailed');
}
export function clearHasPhoneNumber({ commit }) {
  commit('clearHasPhoneNumber');
}
export function clearHasEmailAddress({ commit }) {
  commit('clearHasEmailAddress');
}
export function setIsLoggingOff({ commit }, payload) {
  commit('setIsLoggingOff', payload);
}

export function getEmailAddress({ commit }) {
  return axiosInstance.get('account/getEmailAddress').then((response) => {
    commit('user/updateEmailAddress', response.data, { root: true });
  });
}

export function getBankOptions({ commit }) {
  return axiosInstance.get('idin/bank-options').then((response) => {
    commit('setBankOptions', response.data);
  });
}

export function declineDigitalPopup() {
  return axiosInstance.post('account/decline-digital-pop-up');
}
